import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../../api';

type Planned = {
  line: number; action: 'create' | 'update'; login: string; name: string;
  email: string | null; phone: string | null; notes: string[];
  groupIds: number[]; courseIds: number[];
};
type Rejected = { line: number; who: string; reason: string };
type Preview = {
  dryRun: true; read: number; create: number; update: number;
  planned: Planned[]; rejected: Rejected[];
};

/**
 * Putting an intake in from a spreadsheet.
 *
 * Nothing is written until the plan below has been looked at. A sheet with a
 * shifted column can otherwise rename fifty people in one click, and there is
 * no undo for that.
 */
export function ImportPeople() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async (confirm: boolean) => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('confirm', confirm ? 'true' : 'false');
      const res = await fetch('/api/admin/people/import', {
        method: 'POST', body: form, credentials: 'same-origin',
      });
      const data = await res.json();
      if (!res.ok) throw new ApiError(res.status, data?.error ?? res.statusText);

      if (confirm) {
        setPreview(null);
        setDone(`${data.created} added, ${data.updated} updated.`);
      } else {
        setPreview(data);
        setDone(null);
      }
    } catch (e) { setError((e as ApiError).message); setPreview(null); }
    finally { setBusy(false); }
  };

  return (
    <>
      <nav className="crumbs"><Link to="/admin/people">People</Link> <span aria-hidden>›</span> Import</nav>
      <div className="page-head">
        <h1 className="h1">Import people from a spreadsheet</h1>
        <a className="btn btn--ghost" href="/api/admin/people/export">Export everybody</a>
      </div>

      <p className="muted small">
        Excel or CSV. The columns it looks for are <em>Username, First name, Last name, Email,
        Phone, User type, Cohort, Courses, Status</em> — spelled however your sheet spells them,
        and in any order. Several cohorts or courses in one cell, separated by a semicolon.
        Somebody already in Learnica is matched by username, or by email, and updated rather
        than duplicated.
      </p>
      <p className="notice">
        Leave passwords out of the sheet. A spreadsheet of passwords gets copied and kept;
        new people are sent a link to set their own instead, and a sheet with a password
        column is refused.
      </p>

      {error && <p className="auth__error">{error}</p>}
      {done && <p className="notice">{done}</p>}

      <section className="panel">
        <div className="form">
          <label><span>The spreadsheet</span>
            <input type="file" accept=".xlsx,.csv,.tsv,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setDone(null); setError(null); }} />
          </label>
          <button className="btn btn--primary" disabled={!file || busy} onClick={() => void send(false)}>
            {busy ? 'Reading…' : 'See what it would do'}
          </button>
        </div>
      </section>

      {preview && (
        <>
          <section className="panel">
            <h2 className="h2">
              {preview.create} to add, {preview.update} to update
              {preview.rejected.length > 0 && `, ${preview.rejected.length} to check`}
            </h2>
            <p className="muted small">{preview.read} rows read. Nothing has been written yet.</p>

            {preview.planned.length > 0 && (
              <table className="table">
                <thead><tr>
                  <th>Line</th><th></th><th>Name</th><th>Username</th><th>Email</th><th>Cohorts</th><th>Courses</th><th>Notes</th>
                </tr></thead>
                <tbody>
                  {preview.planned.map((p) => (
                    <tr key={p.line}>
                      <td className="muted">{p.line}</td>
                      <td><span className={`pill ${p.action === 'create' ? 'pill--completed' : 'pill--quiet'}`}>
                        {p.action === 'create' ? 'new' : 'update'}</span></td>
                      <td>{p.name}</td>
                      <td className="muted">{p.login}</td>
                      <td className="muted">{p.email ?? '—'}</td>
                      <td className="muted">{p.groupIds.length || '—'}</td>
                      <td className="muted">{p.courseIds.length || '—'}</td>
                      <td className="warn small">{p.notes.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className="row">
              <button className="btn btn--primary" disabled={busy || !preview.planned.length}
                onClick={() => void send(true)}>
                {busy ? 'Importing…' : `Import these ${preview.planned.length}`}
              </button>
              <button className="btn btn--ghost" onClick={() => setPreview(null)}>Cancel</button>
            </div>
          </section>

          {preview.rejected.length > 0 && (
            <section className="panel">
              <h2 className="h2">Rows to look at ({preview.rejected.length})</h2>
              <p className="muted small">
                These are not imported. Nothing here is guessed at — a row that could mean two
                people is left for somebody to decide.
              </p>
              <table className="table">
                <thead><tr><th>Line</th><th>Who</th><th>Why</th></tr></thead>
                <tbody>
                  {preview.rejected.map((r) => (
                    <tr key={r.line}>
                      <td className="muted">{r.line}</td>
                      <td>{r.who}</td>
                      <td className="warn">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}
    </>
  );
}
